import React, { useRef, useEffect, useMemo, useState } from 'react';

const ScatterPlotViewer = ({ 
  table, 
  xAxis, 
  yAxis, 
  colorBy, 
  searchIndices, 
  colorConfig,
  isGradient,
  isLogScale,
  stats
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Helper for Gradient Interpolation (Yellow to Red)
  const getGradientColor = (val, min, max) => {
    const t = Math.max(0, Math.min(1, (val - min) / (max - min || 1)));
    const r = 255;
    const g = Math.round(255 * (1 - t));
    const b = 0;
    return `rgba(${r}, ${g}, ${b}, 0.7)`;
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const plotData = useMemo(() => {
    if (!table || !xAxis || !yAxis) return null;

    const xCol = table.getChild(xAxis);
    const yCol = table.getChild(yAxis);
    const cCol = table.getChild(colorBy);
    
    if (!xCol || !yCol) return null;

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);
    
    const points = [];
    for (const i of source) {
      const x = xCol.get(i);
      const yRaw = yCol.get(i);
      const c = cCol ? cCol.get(i) : null;

      if (x != null && yRaw != null && !isNaN(x) && !isNaN(yRaw)) {
        if (isLogScale && yRaw <= 0) continue;
        const y = isLogScale ? Math.log10(yRaw) : yRaw;
        
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        points.push({ x, y: yRaw, c, i });
      }
    }

    const xPadding = (maxX - minX) * 0.05 || 1;
    const yPadding = (maxY - minY) * 0.05 || 1;

    return {
      points,
      range: {
        minX: minX - xPadding, maxX: maxX + xPadding,
        minY: minY - yPadding, maxY: maxY + yPadding
      }
    };
  }, [table, xAxis, yAxis, colorBy, searchIndices, isLogScale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !plotData || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    const { points, range } = plotData;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = dimensions.width - padding.left - padding.right;
    const height = dimensions.height - padding.top - padding.bottom;

    const getX = (val) => padding.left + ((val - range.minX) / (range.maxX - range.minX)) * width;
    const getY = (val) => {
      const v = isLogScale ? Math.log10(val) : val;
      return padding.top + height - ((v - range.minY) / (range.maxY - range.minY)) * height;
    };

    ctx.clearRect(0, 0, dimensions.width, dimensions.height);

    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, padding.top + height);
    ctx.lineTo(padding.left + width, padding.top + height);
    ctx.stroke();

    ctx.fillStyle = '#94a3b8';
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    
    for (let i = 0; i <= 4; i++) {
      const val = range.minY + (i / 4) * (range.maxY - range.minY);
      const y = padding.top + height - ((val - range.minY) / (range.maxY - range.minY)) * height;
      const displayVal = isLogScale ? Math.pow(10, val) : val;
      ctx.fillText(displayVal.toPrecision(3), padding.left - 8, y + 3);
    }

    ctx.textAlign = 'center';
    for (let i = 0; i <= 4; i++) {
      const val = range.minX + (i / 4) * (range.maxX - range.minX);
      const x = getX(val);
      ctx.fillText(val.toPrecision(3), x, padding.top + height + 15);
    }

    // Points

    points.forEach(p => {
      const x = getX(p.x);
      const y = getY(p.y);
      
      let color = 'rgba(100, 100, 100, 0.2)';
      
      // Color logic
      if (isGradient && stats.isNumeric) {
        color = getGradientColor(parseFloat(p.c), stats.min, stats.max);
      } else if (colorConfig && colorConfig.colorMap.has(p.c)) {
        const rgba = colorConfig.colorMap.get(p.c);
        color = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, 0.6)`;
      }

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fill();
    });

  }, [plotData, colorConfig, dimensions, isGradient, stats, isLogScale]);
  
  const handleMouseMove = (e) => {
    if (!plotData || dimensions.width === 0) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = dimensions.width - padding.left - padding.right;
    const height = dimensions.height - padding.top - padding.bottom;
    const { range, points } = plotData;

    let closest = null;
    let minDist = 15;

    for (const p of points) {
      const px = padding.left + ((p.x - range.minX) / (range.maxX - range.minX)) * width;
      const pLogY = isLogScale ? Math.log10(p.y) : p.y;
      const py = padding.top + height - ((pLogY - range.minY) / (range.maxY - range.minY)) * height;
      const d = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
      if (d < minDist) {
        minDist = d;
        closest = p;
      }
    }

    if (closest) {
      setHoverInfo({ ...closest, mouseX: x, mouseY: y });
    } else {
      setHoverInfo(null);
    }
  };

  const tooltipStyle = useMemo(() => {
    if (!hoverInfo || dimensions.width === 0) return {};
    const { mouseX, mouseY } = hoverInfo;
    const style = { position: 'absolute', zIndex: 50 };

    if (mouseX > dimensions.width / 2) {
      style.right = dimensions.width - mouseX + 10;
    } else {
      style.left = mouseX + 10;
    }

    if (mouseY > dimensions.height / 2) {
      style.bottom = dimensions.height - mouseY + 10;
    } else {
      style.top = mouseY + 10;
    }

    return style;
  }, [hoverInfo, dimensions]);

  return (
    <div ref={containerRef} className="w-full h-full relative cursor-crosshair bg-white overflow-hidden">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverInfo(null)}
      />
      

      {hoverInfo && (
        <div 
          className="pointer-events-none bg-white p-2 rounded-xl text-[10px] border border-gray-800 min-w-[140px]"
          style={tooltipStyle}
        >
          <div className="border-b border-gray-500 mb-1 pb-1 truncate max-w-[180px]">
             <span className="font-bold">{colorBy}</span>: {String(hoverInfo.c)}
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-600 font-bold">X:</span>
            <span>{hoverInfo.x.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-600 font-bold">Y:</span>
            <span>{hoverInfo.y.toLocaleString()}</span>
          </div>
        </div>
      )}
      
      {!plotData?.points.length && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs italic">
          No numeric data available
        </div>
      )}
    </div>
  );
};

export default ScatterPlotViewer;